import {
    increaseTotalNumSelectedCities,
    increaseTotalNumSelectedRoomLists,
    queryStringChanged,
} from '../../mutators/roomPanelMetricsMutators';
import getStore from '../../store/store';
import RoomFilterIcon from '../RoomSelector/RoomFilterIcon';
import { RoomFilterType } from '../RoomSelector/RoomFilterType';
import RoomFilterWellItemViewState from '../RoomSelector/RoomFilterWellItemViewState';
import { observer } from 'mobx-react-lite';
import loc from 'owa-localize';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import * as React from 'react';
import {
    CompositePicker,
    CompositePickerProps,
    isParentOption,
    PickerOption,
    PickerOptionsSection,
    ParentOption,
    recomputeIndexForOptionsAndUpdate,
} from 'owa-calendar-composite-picker';
import {
    roomListSectionCities,
    roomListSectionRoomLists,
    citiesSectionAriaLabel,
    roomListsSectionAriaLabel,
} from './CalendarRoomListPicker.locstring.json';
import { addChildrenInRoomListPicker } from '../../mutators/calendarRoomMutators';
import { clearFilters } from '../../mutators/capabilityFilterMutators';
import getRoomListsFromPlacesApi from 'owa-room-selection/lib/services/getRoomListsFromPlacesApi';
import { useModeStyles } from 'owa-react-hooks/lib/useModeStyles';
import Fuse from 'fuse.js';

const styles = {
    base: require('./CapabilityFilters.scss'),
    desktop: require('./CapabilityFilters.outlookdesktop.scss'),
    mac: require('./CapabilityFilters.macoutlook.scss'),
};

export interface RoomListPickerProps
    extends Omit<CompositePickerProps, 'onChange' | 'viewState' | 'allowNullSelection'> {
    onChanged: (items: RoomFilterWellItemViewState[]) => void;
}

export default observer(function RoomListPicker(props: RoomListPickerProps) {
    const { getClass } = useClientStyles(styles);
    const { getModeAwareClass } = useModeStyles(getClass);

    const onChange = (pickerOption: PickerOption) => {
        clearFilters();
        props.onChanged([
            {
                displayText: pickerOption?.displayName,
                filterType: RoomFilterType?.RoomList,
                queryableId: pickerOption?.id,
                isContextMenuOpen: null,
                isValid: null,
                isSelected: null,
            },
        ]);
        increaseTotalNumSelectedRoomLists();
        if (pickerOption.parentId) {
            increaseTotalNumSelectedCities();
        }
    };

    const renderOption = (option: PickerOption) => {
        if (isParentOption(option)) {
            return <span className={getClass('roomListName')}>{option.displayName}</span>;
        }

        return (
            <div title={option.displayName} className={getClass('roomListResult')}>
                <RoomFilterIcon
                    colorScheme={props.colorScheme}
                    roomFilterType={RoomFilterType.RoomList}
                    className={getClass('roomListResultIcon')}
                />
                <span className={getClass('roomListName')}>{option.displayName}</span>
            </div>
        );
    };

    return (
        <CompositePicker
            viewState={getStore().roomListPickerViewState}
            filterFunction={filterFunction}
            onChange={onChange}
            onParentOptionExpanded={onParentOptionExpanded}
            alwaysShowAllSections={true}
            ariaLabels={{
                leafOptionsAriaLabel: roomListsSectionAriaLabel,
                parentOptionsAriaLabel: citiesSectionAriaLabel,
            }}
            styles={{
                sectionHeader: getModeAwareClass('roomListPickerSectionHeader'),
                textField: getClass('roomListPickerTextField'),
                optionsContainer: getClass('roomListPickerOptionsContainer'),
                callout: getClass('roomListPickerCallout'),
                optionButtonFlexContainer: getClass('optionButtonFlexContainer'),
                optionButton: getClass('optionButton'),
                optionContainer: getClass('optionContainer'),
                highlightedOptionButton: getClass('optionButtonHighlighted'),
            }}
            renderOption={renderOption}
            {...props}
        />
    );
});

function onParentOptionExpanded(option: ParentOption) {
    getRoomListsFromPlacesApi(option.displayName).then(result => {
        addChildrenInRoomListPicker(result, option);
        recomputeIndexForOptionsAndUpdate(getStore().roomListPickerViewState);
    });
}

/**
 * Override the filter function to do server-side filtering
 * @param sections The original sections
 * @param textFieldValue The current query string
 */
async function filterFunction(sections: PickerOptionsSection[], textFieldValue: string) {
    queryStringChanged();
    const citiesLabel = loc(roomListSectionCities);
    const roomListsLabel = loc(roomListSectionRoomLists);
    const normalizedTextFieldValue = textFieldValue.toLocaleLowerCase();
    const roomLists = getStore().roomListViewState.roomLists || [];
    let roomListsAsPickerOptions = null;
    let citiesAsPickerOptions = null;

    const filterOptions = {
        threshold: 0.3,
        shouldSort: true,
        // require match of 2 characters unless search string is only 1 character long
        minMatchCharLength: textFieldValue.length > 1 ? 2 : 1,
    };

    const roomListsFilter = new Fuse(roomLists, { ...filterOptions, keys: ['name' as 'name'] });
    const citiesFilter = new Fuse(sections?.[1].parentOptions, {
        ...filterOptions,
        keys: ['displayName' as 'displayName'],
    });

    // Filtering through all the room lists using fuzzy search
    roomListsAsPickerOptions = roomListsFilter.search(normalizedTextFieldValue);

    // Filtering through all the cities which are part of sections[1] using fuzzy search
    citiesAsPickerOptions = citiesFilter.search(normalizedTextFieldValue);

    roomListsAsPickerOptions = roomListsAsPickerOptions.map((roomList, index) => ({
        id: roomList.email,
        index: index,
        displayName: roomList.name,
        account: roomListsLabel,
        isShown: true,
    }));

    return [
        {
            account: roomListsLabel,
            isPrimaryAccount: false,
            leafOptions: roomListsAsPickerOptions,
            parentOptions: [],
        },
        {
            account: citiesLabel,
            isPrimaryAccount: false,
            leafOptions: [],
            parentOptions: citiesAsPickerOptions,
        },
    ];
}
